/**
 * The catalogue as a workbook (docs/01-architektur.md, Excel-Mappe): sheets „Speisen“,
 * „Kategorien“, „Allergene“ and the sheet „Erklärungen“. The app writes it (empty template or the whole catalogue)
 * and reads it back: `planImport` says what is new, changed, the same or wrong, before anything is
 * written. App and server run the same plan; the server applies it.
 *
 * Rules: a row belongs to the dish with the same number; without number to the one with the same
 * name; else it is new and gets the next free number. Rows missing in the workbook stay in the
 * catalogue; only „Archiviert: ja“ takes a dish out. An empty allergen cell leaves the allergens as
 * they are, „keine“ says none. An unknown code or category is an error of that row (S12); the other
 * rows still go. New categories come in through the sheet „Kategorien“, in its order. The texts (S14)
 * through the sheet „Textbausteine“: a row like a text is that text, a heading known once changes that
 * text's words, any other row is a new text at the end; none go. The business's own words in further
 * languages (S36) through the sheet „Übersetzungen“: a German text and a column per language; a
 * filled cell is that translation, an empty one leaves it as it is.
 */
import { DEFAULT_ALLERGENS, KIND_LABELS, codesText, isCode, isLmiv, normCode, parseCodes } from "./allergens.ts";
import type { Allergen, AllergenKind } from "./allergens.ts";
import { MAX_CATEGORIES, cleanCategory, resolveCategory } from "./categories.ts";
import { DRINK_START, LIMITS, MAX_NUMBER, cleanDish, cleanTags, isDrink, kindOf as dishKind, parseVariants, variantsText } from "./dishes.ts";
import type { Dish, DishInput, DishKind } from "./dishes.ts";
import { euroCell, parsePrice, plainPrice } from "./money.ts";
import { fold } from "./search.ts";
import { TEXT_LIMITS } from "./texts.ts";
import type { CardText } from "./texts.ts";
import { LANGS, LANG_LABELS, LANG_NATIVE, isLang, translationState } from "./translation.ts";
import type { Lang, Translation, TranslationsInput } from "./translation.ts";
import { PHRASE_GROUP_LABELS, PHRASE_LIMITS, phraseKey } from "./phrases.ts";
import { phraseOf } from "./phrase-base.ts";
import type { PhraseGroup, Phrases } from "./phrases.ts";

export type Cell = string | number | boolean | Date | null | undefined;
export interface Sheet {
  name: string;
  rows: Cell[][];
}

export const SHEET_DISHES = "Speisen";
/** The drinks (S22): the same columns as „Speisen“; in every workbook, like the template, also of a business without drinks. */
export const SHEET_DRINKS = "Getränke";
export const SHEET_ALLERGENS = "Allergene";
export const SHEET_CATEGORIES = "Kategorien";
export const SHEET_GUIDE = "Erklärungen";
/** The texts of the catalogue (S14): lines on a menu that are no dish. */
export const SHEET_TEXTS = "Textbausteine";
/** The business's own words in further languages (S36, 31.10.2026): the dictionary, a column per language. */
export const SHEET_PHRASES = "Übersetzungen";

interface Column {
  key: string;
  head: string;
  aliases: string[];
  required: boolean;
  format: string;
  example: string;
  effect: string;
}

export const DISH_COLUMNS: readonly Column[] = [
  { key: "number", head: "Nr.", aliases: ["nr", "nummer", "id", "#", "no"], required: false, format: "ganze Zahl von 1 bis 99999", example: "12", effect: "Kennt die App die Nummer, ändert die Zeile diese Speise. Leer: die Speise mit demselben Namen, sonst eine neue mit der nächsten freien Nummer." },
  { key: "name", head: "Name", aliases: ["gericht", "speise", "bezeichnung"], required: true, format: "Text, höchstens 120 Zeichen", example: "Gambas Pil Pil", effect: "So steht die Speise auf der Speisekarte." },
  { key: "description", head: "Beschreibung", aliases: ["kartentext", "text", "description"], required: false, format: "Text, höchstens 600 Zeichen", example: "Scharfe Garnelen in Knoblauch-Chili-Öl", effect: "Steht klein unter dem Namen. Leer: keine Beschreibung." },
  { key: "category", head: "Kategorie", aliases: ["gruppe", "category", "rubrik"], required: false, format: "Text, Ebenen mit „/“ getrennt", example: "Warm / Meeresfrüchte", effect: "Eine aus dem Blatt „Kategorien“ (oder schon in der App). Zum Suchen und Filtern; „Abschnitte aus Kategorien“ macht aus der ersten Ebene Abschnitte. Leer: ohne Kategorie." },
  { key: "tags", head: "Tags", aliases: ["merkmale", "schlagworte", "tag"], required: false, format: "durch Komma getrennt", example: "vegetarisch, Scharf", effect: "Zum Suchen und Filtern. vegan, vegetarisch, glutenfrei, laktosefrei und scharf stehen auch auf der Speisekarte." },
  { key: "allergens", head: "Allergene", aliases: ["allergene und zusatzstoffe", "kennzeichnung", "allergens", "allergens_notes", "zusatzstoffe"], required: false, format: "Kürzel aus dem Blatt „Allergene“, durch Komma getrennt; „keine“", example: "a, c, j", effect: "Leer lässt die Angaben, wie sie sind (oder „nicht gepflegt“ bei einer neuen Speise). „keine“: enthält keine." },
  { key: "price", head: "Preis", aliases: ["preis €", "preis (€)", "preis in euro", "price", "price_eur", "verkaufspreis", "€"], required: false, format: "Zahl in Euro, z. B. 7,80", example: "7,80", effect: "Der übliche Preis, Vorschlag für jede neue Speisekarte. Leer: ohne Preis." },
  { key: "priceLabel", head: "Preis-Bezeichnung", aliases: ["preisbezeichnung", "preis bezeichnung", "preis fuer", "portion", "groesse", "menge"], required: false, format: "Text, höchstens 40 Zeichen", example: "Ración", effect: "Wofür der Preis gilt, etwa „Ración“ oder „0,2 l“. Dann steht der Preis in einer eigenen Zeile unter der Speise. Leer: der Preis steht beim Namen." },
  { key: "variants", head: "Weitere Preise", aliases: ["weitere preise", "varianten", "variante", "preise", "portionen"], required: false, format: "Bezeichnung Preis (Kürzel), mehrere mit „;“ getrennt; höchstens 6", example: "½ Ración 11,50; mit Feta 7,80 (g)", effect: "Jeder steht in einer eigenen Zeile unter der Speise, mit seinem Preis. Kürzel in Klammern kommen zu denen der Speise dazu, etwa „mit Thunfisch 7,80 (d)“. Leer: keine weiteren Preise." },
  { key: "archived", head: "Archiviert", aliases: ["entfernt", "ausgeblendet", "archiv"], required: false, format: "ja oder nein", example: "nein", effect: "ja: nicht mehr zur Auswahl, alte Speisekarten behalten die Speise. nein: wieder zur Auswahl. Leer: wie bisher." },
];

export const CATEGORY_COLUMNS: readonly Column[] = [
  { key: "name", head: "Kategorie", aliases: ["name", "bezeichnung", "category"], required: true, format: "Text, Ebenen mit „ / “ getrennt", example: "Warm / Meeresfrüchte", effect: "Neue Kategorien kommen in dieser Reihenfolge dazu. In dieser Reihenfolge stehen die Filter und die Abschnitte aus Kategorien. Umbenennen und Entfernen geht nur in der App." },
  { key: "for", head: "für", aliases: ["fuer", "art", "fuer was"], required: false, format: "Speisen oder Getränke", example: "Getränke", effect: "Getränke: eine Kategorie der Getränke. Leer: eine Kategorie der Speisen." },
];

export const ALLERGEN_COLUMNS: readonly Column[] = [
  { key: "code", head: "Kürzel", aliases: ["kuerzel", "code", "zeichen", "nr", "buchstabe"], required: true, format: "1–4 Buchstaben oder Ziffern", example: "a", effect: "Steht bei der Speise auf der Speisekarte, etwa „Aioli con pan a, c, j“." },
  { key: "name", head: "Bezeichnung", aliases: ["name", "allergen", "zusatzstoff"], required: true, format: "Text", example: "Glutenhaltiges Getreide", effect: "Steht in der Legende unten auf der Speisekarte." },
  { key: "kind", head: "Art", aliases: ["typ", "kind"], required: false, format: "Allergen oder Zusatzstoff", example: "Allergen", effect: "Allergene und Zusatzstoffe stehen in der Legende getrennt. Leer: Allergen." },
];

export const TEXT_COLUMNS: readonly Column[] = [
  { key: "title", head: "Überschrift", aliases: ["ueberschrift", "titel", "title"], required: false, format: `Text, höchstens ${TEXT_LIMITS.title} Zeichen`, example: "Tapa del día", effect: "Steht kursiv in der Akzentfarbe, darunter der Text; ohne Text eine fette Zeile. Eine Überschrift, die schon einmal in der App steht, ändert den Text dieses Texts." },
  { key: "body", head: "Text", aliases: ["inhalt", "body", "textzeile"], required: false, format: `Text, höchstens ${TEXT_LIMITS.body} Zeichen`, example: "Fragen Sie nach unseren Tagestapas!", effect: "Die Zeile auf der Speisekarte, ohne Nummer, Preis und Allergene. Überschrift oder Text genügt." },
];

export const PHRASE_COLUMNS: readonly Column[] = [
  { key: "text", head: "Deutsch", aliases: ["de", "german", "original"], required: true, format: "Text, wie er in der App steht", example: "Vorspeisen", effect: "Der deutsche Text; seine Übersetzung gilt überall, wo er steht. Den deutschen Text ändert man in der App." },
  { key: "group", head: "Herkunft", aliases: ["woher", "art", "source"], required: false, format: "nur zur Orientierung", example: "Speisekarten", effect: "Wo der Text zuerst vorkommt: Speisekarten, Kategorien, Tags, Allergene und Zusatzstoffe, Textbausteine, Preisbezeichnungen, Fuß. Wird beim Einlesen nicht gelesen." },
];
/** The head of a language's column in „Übersetzungen“: „Englisch (en)“. */
export const phraseHead = (lang: Lang) => `${LANG_LABELS[lang]} (${lang})`;

/** The sheet „Erklärungen“: every column of both sheets with its rules. */
export function guideRows(): string[][] {
  const rows: string[][] = [["Blatt", "Spalte", "Pflicht", "Format", "Beispiel", "Was sie bewirkt"]];
  for (const [sheet, cols] of [[SHEET_DISHES, DISH_COLUMNS], [SHEET_CATEGORIES, CATEGORY_COLUMNS], [SHEET_ALLERGENS, ALLERGEN_COLUMNS], [SHEET_TEXTS, TEXT_COLUMNS]] as const) {
    for (const c of cols) rows.push([sheet, c.head, c.required ? "ja" : "", c.format, c.example, c.effect]);
  }
  rows.push(["", "", "", "", "", ""]);
  rows.push(["Regeln", "", "", "", "", "Zeilen, die in der Mappe fehlen, bleiben im Katalog. Ein Fehler in einer Zeile lässt nur diese Zeile aus. Vor dem Übernehmen zeigt die App, was neu, geändert und fehlerhaft ist."]);
  rows.push(["Kürzel", "", "", "", "", "Ein Kürzel, das eine Speise nennt, muss im Blatt „Allergene“ oder schon in der App stehen. Neue Kürzel im Blatt „Allergene“ kommen dazu; Kürzel entfernen geht nur in der App."]);
  rows.push(["Kategorien", "", "", "", "", "Ebenso jede Kategorie: Sie muss im Blatt „Kategorien“ oder schon in der App stehen, sonst bleibt die Zeile aus."]);
  rows.push([SHEET_DRINKS, "", "", "", "", "Getränke stehen im Blatt „Getränke“, mit denselben Spalten wie „Speisen“. Die Menge gehört zum Preis: „Preis-Bezeichnung“ etwa „0,3 l“, „Weitere Preise“ etwa „0,5 l 4,90“. Die Nummern sind über Speisen und Getränke eindeutig; neue Getränke ohne Nummer bekommen die nächste freie ab 100. Die Kategorie kommt aus den Kategorien „für Getränke“."]);
  rows.push([SHEET_TEXTS, "", "", "", "", "Textbausteine wie „Tapa del día – Fragen Sie nach unseren Tagestapas!“ oder „Gerne bieten wir alle Salate auch in klein an.“: Zeilen ohne Nummer und Preis, die man in der App auf jede Speisekarte setzt. Eine Zeile, die genau so schon in der App steht, bleibt; eine bekannte Überschrift mit anderem Text ändert diesen Text; jede andere Zeile kommt als neuer Text dazu, ans Ende der Liste. Entfernen und Umstellen geht nur in der App."]);
  rows.push(["Sprachen", "", "", "", "", "Mit einer weiteren Sprache (Konto → Sprachen) haben Speisen und Getränke am Ende die Spalten „Name (en)“, „Beschreibung (en)“ und „Stand (en)“. Leer lassen, was nicht übersetzt ist; bei Getränken bleibt der Name meist, wie er ist. Fehlen die Spalten, bleiben die Übersetzungen in der App, wie sie sind."]);
  rows.push(["Stand", "Stand (en) …", "", "nicht durchgesehen, durchgesehen, von Hand, veraltet, Vorgabe", "durchgesehen", STATE_EFFECT]);
  for (const c of PHRASE_COLUMNS) rows.push([SHEET_PHRASES, c.head, c.required ? "ja" : "", c.format, c.example, c.effect]);
  rows.push([SHEET_PHRASES, "Englisch (en) …", "", "Text", "Starters", "Je weitere Sprache eine Spalte, daneben ihr „Stand (en)“. Alles auf den Speisekarten, was keine Speise ist: Titel, Abschnitte, Hinweise, Gänge, Preisbezeichnungen, Kategorien, Tags, eigene Allergene, Textbausteine, der Fuß; jeder deutsche Text einmal. Eine geänderte Zelle ist die Übersetzung (von Hand, das Übersetzen in der App lässt sie stehen); eine leere lässt sie, wie sie ist. Wegnehmen geht nur in der App."]);
  return rows;
}

const head = (cols: readonly Column[]) => cols.map((c) => c.head);

/**
 * „Stand (en)“ (S61, Martin 02.11.2026: „Spalte dazu“): how far a translation is, as the app shows it;
 * read back, „durchgesehen“ at a translation left as it is is the app's „Passt“. Anything else written
 * there changes nothing; a translation changed in the workbook is by hand anyway.
 */
const STATE_EFFECT = "Wie weit die Übersetzung ist: „nicht durchgesehen“ (vom Übersetzer), „durchgesehen“, „von Hand“, „veraltet“ (das Deutsche hat sich seitdem geändert), „Vorgabe“ (aus dem Grundvokabular der App). Beim Einlesen: „durchgesehen“ (auch „passt“, „ok“) bei einer unveränderten Übersetzung ist dasselbe wie „Passt“ in der App; eine geänderte Übersetzung gilt ohnehin als von Hand. Was sonst dasteht, ändert nichts.";
export const stateHead = (lang: Lang) => `Stand (${lang})`;
const BY_WORDS: Record<Translation["by"] | "base", string> = { ai: "nicht durchgesehen", checked: "durchgesehen", hand: "von Hand", base: "Vorgabe" };
/** Whether a cell of „Stand“ says it was looked through. */
const seenIn = (cell: Cell) => ["durchgesehen", "passt", "ok", "gepruft", "geprueft"].includes(fold(cellText(cell)).replace(/[^a-z]/g, ""));
/** A dish's translation's state in words; empty without one. */
function dishState(d: Dish, lang: Lang): string {
  const t = d.translations?.[lang];
  if (!t) return "";
  const state = translationState(d, lang);
  return state === "stale" ? "veraltet" : state === "missing" ? "unvollständig" : BY_WORDS[t.by];
}

/** The categories as rows; with drinks (S22) a column „für“ says which are the drinks'. */
const categoryRows = (categories: readonly string[], drinkCategories: readonly string[]): Cell[][] => [head(CATEGORY_COLUMNS), ...categories.map((c) => [c, "Speisen"]), ...drinkCategories.map((c) => [c, "Getränke"])];

const plainRow = (d: Dish): Cell[] => [d.number, d.name, d.description || null, d.category || null, d.tags.join(", ") || null, codesText(d.allergens) || null, euroCell(d.price), d.priceLabel || null, variantsText(d.variants) || null, d.archived ? "ja" : null];

/** The columns of a further language (S36), after the others: „Name (en)“, „Beschreibung (en)“. */
export const translationHeads = (lang: Lang) => [`Name (${lang})`, `Beschreibung (${lang})`, stateHead(lang)];
const translationCells = (d: Dish, langs: readonly Lang[]): Cell[] => langs.flatMap((l) => [d.translations?.[l]?.name || null, d.translations?.[l]?.description || null, dishState(d, l) || null]);

/** The languages of a head row's further columns, with where their name, description and state stand. */
function translationColumns(row: readonly Cell[]): Map<Lang, { name?: number; description?: number; state?: number }> {
  const out = new Map<Lang, { name?: number; description?: number; state?: number }>();
  row.forEach((cell, i) => {
    // The head folded: „Name (en)“ and „Name (Englisch)“ read „name en“, „name englisch“.
    const m = /^(name|beschreibung|stand) ([a-z]+)$/.exec(headKey(cell));
    if (!m) return;
    const lang = isLang(m[2]) ? m[2] : LANGS.find((l) => fold(LANG_LABELS[l]) === m[2]);
    if (!lang) return;
    out.set(lang, { ...out.get(lang), [m[1] === "name" ? "name" : m[1] === "stand" ? "state" : "description"]: i });
  });
  return out;
}

/**
 * The whole catalogue as sheets, ready to write; the drinks (S22) only when there are any. With
 * further languages (S36) the dishes have their columns, also for a language switched off that a
 * dish still has.
 */
/**
 * The sheet „Übersetzungen“ (S36): every text of the dictionary once, where it comes from, a column
 * per language switched on or that has some; none without a language.
 */
function phraseSheet(words: { list: readonly { text: string; group: PhraseGroup }[]; phrases: Phrases }, languages: readonly Lang[]): Sheet[] {
  const langs = LANGS.filter((l) => languages.includes(l) || Object.values(words.phrases).some((p) => p[l]));
  if (!langs.length) return [];
  // The base vocabulary's too, its state „Vorgabe“; read back unchanged it stays the base (Martin, 01.11.2026).
  return [
    {
      name: SHEET_PHRASES,
      rows: [
        [...head(PHRASE_COLUMNS), ...langs.flatMap((l) => [phraseHead(l), stateHead(l)])],
        ...words.list.map((p) => {
          const found = langs.map((l) => phraseOf(p.text, l, words.phrases));
          return [p.text, PHRASE_GROUP_LABELS[p.group], ...found.flatMap((f) => [f?.text || null, f ? BY_WORDS[f.by] : null])];
        }),
      ],
    },
  ];
}

export function exportSheets(dishes: readonly Dish[], allergens: readonly Allergen[], categories: readonly string[], drinkCategories: readonly string[] = [], languages: readonly Lang[] = [], texts: readonly CardText[] = [], words: { list: readonly { text: string; group: PhraseGroup }[]; phrases: Phrases } | null = null): Sheet[] {
  const sorted = [...dishes].sort((a, b) => a.number - b.number);
  const langs = LANGS.filter((l) => languages.includes(l) || dishes.some((d) => d.translations?.[l]));
  const dishHead = [...head(DISH_COLUMNS), ...langs.flatMap(translationHeads)];
  const dishRow = (d: Dish) => [...plainRow(d), ...translationCells(d, langs)];
  return [
    { name: SHEET_DISHES, rows: [dishHead, ...sorted.filter((d) => !isDrink(d)).map(dishRow)] },
    { name: SHEET_CATEGORIES, rows: categoryRows(categories, drinkCategories) },
    { name: SHEET_ALLERGENS, rows: [head(ALLERGEN_COLUMNS), ...allergens.map((a) => [a.code, a.name, KIND_LABELS[a.kind]])] },
    { name: SHEET_DRINKS, rows: [dishHead, ...sorted.filter(isDrink).map(dishRow)] },
    { name: SHEET_TEXTS, rows: [head(TEXT_COLUMNS), ...texts.map((x) => [x.title || null, x.body || null])] },
    ...(words ? phraseSheet(words, languages) : []),
  ];
}

/** An empty template: the heads of „Speisen“ and „Getränke“ (S22), the business's categories and allergens to look them up. */
export function templateSheets(allergens: readonly Allergen[] = DEFAULT_ALLERGENS, categories: readonly string[] = [], drinkCategories: readonly string[] = []): Sheet[] {
  return [
    { name: SHEET_DISHES, rows: [head(DISH_COLUMNS)] },
    { name: SHEET_CATEGORIES, rows: categoryRows(categories, drinkCategories) },
    { name: SHEET_ALLERGENS, rows: [head(ALLERGEN_COLUMNS), ...allergens.map((a) => [a.code, a.name, KIND_LABELS[a.kind]])] },
    { name: SHEET_DRINKS, rows: [head(DISH_COLUMNS)] },
    { name: SHEET_TEXTS, rows: [head(TEXT_COLUMNS)] },
  ];
}

// ---------------------------------------------------------------------------

export interface ImportIssue {
  sheet: string;
  line: number;
  message: string;
}

export interface PlannedNew {
  line: number;
  input: DishInput & { number: number };
}

export interface PlannedChange {
  line: number;
  id: string;
  number: number;
  name: string;
  /** What changes, readable: „Preis 5,20 → 5,50“. */
  changes: string[];
  input: DishInput & { number: number };
}

export interface ImportPlan {
  /** New categories from the sheet „Kategorien“, and the list after the import. */
  categories: { added: string[]; result: string[] };
  /** The same for the categories of the drinks (S22). */
  drinkCategories: { added: string[]; result: string[] };
  allergens: { added: Allergen[]; changed: { before: Allergen; after: Allergen }[]; result: Allergen[] };
  dishes: { added: PlannedNew[]; changed: PlannedChange[]; unchanged: number };
  /** The business's own words (S36): translations new or changed by the sheet „Übersetzungen“. */
  phrases: { changed: { line: number; text: string; lang: Lang; before: string; after: string }[]; unchanged: number; /** „durchgesehen“ in „Stand“ (S61) at the model's translation left as it is. */ checked: { line: number; text: string; lang: Lang }[] };
  /** The texts (S14): new ones, ones whose words change by their known heading, and the list after the import. */
  texts: { added: { line: number; title: string; body: string }[]; changed: { line: number; id: string; title: string; before: string; after: string }[]; unchanged: number; result: (Omit<CardText, "id"> & { id?: string })[] };
  errors: ImportIssue[];
  /** Rows read per sheet (without heads and empty rows). */
  read: { dishes: number; drinks: number; allergens: number; categories: number; texts: number; phrases: number };
  /** No sheet with dishes or allergens was found. */
  empty: boolean;
}

const cellText = (c: Cell): string => (c === null || c === undefined ? "" : c instanceof Date ? c.toLocaleDateString("de-DE") : String(c).trim());
const headKey = (c: Cell) => fold(cellText(c)).replace(/\s+/g, " ");

/** The columns a head row names, by key; null when the row does not look like a head. */
function columnsOf(row: Cell[], cols: readonly Column[]): Map<string, number> | null {
  const found = new Map<string, number>();
  row.forEach((cell, i) => {
    const key = headKey(cell);
    if (!key) return;
    const col = cols.find((c) => fold(c.head) === key || c.aliases.some((a) => fold(a) === key));
    if (col && !found.has(col.key)) found.set(col.key, i);
  });
  return cols.filter((c) => c.required).every((c) => found.has(c.key)) ? found : null;
}

/** The head row among the first ten and its columns. */
function findHead(sheet: Sheet, cols: readonly Column[]): { at: number; columns: Map<string, number> } | null {
  for (let i = 0; i < Math.min(10, sheet.rows.length); i++) {
    const columns = columnsOf(sheet.rows[i] ?? [], cols);
    if (columns) return { at: i, columns };
  }
  return null;
}

const isEmpty = (row: Cell[]) => row.every((c) => cellText(c) === "");

function yesNo(c: Cell): boolean | undefined | "error" {
  if (typeof c === "boolean") return c;
  const t = cellText(c).toLowerCase();
  if (t === "") return undefined;
  if (["ja", "j", "x", "1", "yes", "true", "wahr"].includes(t)) return true;
  if (["nein", "n", "0", "-", "no", "false", "falsch"].includes(t)) return false;
  return "error";
}

function kindOf(c: Cell): AllergenKind | "error" {
  const t = fold(cellText(c));
  if (t === "" || t.startsWith("allergen")) return "allergen";
  if (t.startsWith("zusatz") || t.startsWith("additiv")) return "additive";
  return "error";
}

const sameAllergen = (a: Allergen, b: Allergen) => a.name === b.name && a.kind === b.kind;

/** The plan for a workbook against the current catalogue. Pure: nothing is written. */
export function planImport(sheets: readonly Sheet[], current: { dishes: readonly Dish[]; allergens: readonly Allergen[]; categories: readonly string[]; drinkCategories?: readonly string[]; drinkStart?: number; texts?: readonly CardText[]; phrases?: Phrases }): ImportPlan {
  const errors: ImportIssue[] = [];
  // „Übersetzungen“ (S36) is no other sheet, whatever its columns; without a name (a CSV) by its head:
  // „Deutsch“ and a language, and no head of dishes or texts.
  const words =
    sheets.find((s) => /^uebersetz|^translation/.test(fold(s.name))) ??
    sheets.find((s) => !s.name && findHead(s, DISH_COLUMNS) === null && textHead(s) === null && (findHead(s, PHRASE_COLUMNS) ? (s.rows[findHead(s, PHRASE_COLUMNS)!.at] ?? []).some((c) => langOfHead(c)) : false));
  const guide = (s: Sheet) => fold(s.name).startsWith("erklaer") || s === words;
  const allergenSheet = sheets.find((s) => /allergen|zusatz/.test(fold(s.name)));
  const categorySheet = sheets.find((s) => /^kategorie/.test(fold(s.name)));
  const named = sheets.find((s) => /^text/.test(fold(s.name)));
  // The drinks (S22) before the dishes: „Getränkekarte“ is not a sheet of dishes.
  const drinkSheet = sheets.find((s) => /getraenk|getrank|drink/.test(fold(s.name)) && s !== allergenSheet && s !== categorySheet);
  const dishSheet =
    sheets.find((s) => /gericht|speise|karte|katalog/.test(fold(s.name)) && s !== allergenSheet && s !== categorySheet && s !== drinkSheet && s !== named) ??
    sheets.find((s) => s !== allergenSheet && s !== categorySheet && s !== drinkSheet && s !== named && !guide(s) && findHead(s, DISH_COLUMNS) !== null);
  // The texts by their name, else a sheet without one (a CSV) that names their columns and is no other.
  const textSheet = named ?? sheets.find((s) => s !== allergenSheet && s !== categorySheet && s !== drinkSheet && s !== dishSheet && !guide(s) && textHead(s) !== null);

  // --- Kategorien: new names come in, in the order of the sheet; none go, none are renamed.
  const categories = [...current.categories];
  const drinkCategories = [...(current.drinkCategories ?? [])];
  const addedCategories: string[] = [];
  const addedDrinkCategories: string[] = [];
  let readCategories = 0;
  if (categorySheet) {
    const h = findHead(categorySheet, CATEGORY_COLUMNS);
    if (!h) errors.push({ sheet: categorySheet.name, line: 1, message: "Keine Kopfzeile mit „Kategorie“ gefunden." });
    else {
      categorySheet.rows.slice(h.at + 1).forEach((row, i) => {
        const line = h.at + 2 + i;
        if (isEmpty(row)) return;
        readCategories++;
        const name = cleanCategory(cellText(row[h.columns.get("name") ?? -1]));
        if (name === "") return;
        const forWhat = fold(cellText(row[h.columns.get("for") ?? -1]));
        if (forWhat !== "" && !/^(speise|gericht|essen|getraenk|getrank|drink)/.test(forWhat)) return void errors.push({ sheet: categorySheet.name, line, message: `für „${cellText(row[h.columns.get("for") ?? -1])}“: Speisen oder Getränke` });
        const drink = /^(getraenk|getrank|drink)/.test(forWhat);
        const [list, added] = drink ? [drinkCategories, addedDrinkCategories] : [categories, addedCategories];
        if ("value" in resolveCategory(name, list)) return;
        if (list.length >= MAX_CATEGORIES) return void errors.push({ sheet: categorySheet.name, line, message: `Höchstens ${MAX_CATEGORIES} Kategorien` });
        list.push(name);
        added.push(name);
      });
    }
  }

  // --- Allergene: new codes come in, known ones may change their name or kind (not the 14 of the LMIV); none go.
  const result = current.allergens.map((a) => ({ ...a }));
  const added: Allergen[] = [];
  const changedAllergens: { before: Allergen; after: Allergen }[] = [];
  let readAllergens = 0;
  if (allergenSheet) {
    const h = findHead(allergenSheet, ALLERGEN_COLUMNS);
    if (!h) errors.push({ sheet: allergenSheet.name, line: 1, message: "Keine Kopfzeile mit „Kürzel“ und „Bezeichnung“ gefunden." });
    else {
      const seen = new Map<string, number>();
      allergenSheet.rows.slice(h.at + 1).forEach((row, i) => {
        const line = h.at + 2 + i;
        if (isEmpty(row)) return;
        readAllergens++;
        const at = (key: string) => row[h.columns.get(key) ?? -1];
        const code = normCode(cellText(at("code")));
        const name = cellText(at("name")).replace(/\s+/g, " ");
        const kind = kindOf(at("kind"));
        const issue = (message: string) => errors.push({ sheet: allergenSheet.name, line, message });
        if (!isCode(code)) return issue(`Kürzel „${cellText(at("code"))}“: 1–4 Buchstaben oder Ziffern`);
        if (name === "" || name.length > 80) return issue(`Kürzel „${code}“: Bitte eine Bezeichnung (höchstens 80 Zeichen)`);
        if (kind === "error") return issue(`Art „${cellText(at("kind"))}“: Allergen oder Zusatzstoff`);
        if (seen.has(code)) return issue(`Das Kürzel „${code}“ steht schon in Zeile ${seen.get(code)}`);
        seen.set(code, line);
        // The 14 of the LMIV are fixed: a row of theirs, however named, changes nothing.
        if (isLmiv(code)) return;
        const after = { code, name, kind };
        const known = result.find((a) => a.code === code);
        if (!known) {
          if (result.length >= 60) return issue("Höchstens 60 Allergene und Zusatzstoffe");
          result.push(after);
          added.push(after);
        } else if (!sameAllergen(known, after)) {
          changedAllergens.push({ before: { ...known }, after });
          Object.assign(known, after);
        }
      });
    }
  }
  const codes = new Set(result.map((a) => a.code));

  // --- Speisen and Getränke (S22): the numbers are one set over both.
  const addedDishes: PlannedNew[] = [];
  const changed: PlannedChange[] = [];
  let unchanged = 0;
  const read = { dishes: 0, drinks: 0 };
  const byNumber = new Map(current.dishes.map((d) => [d.number, d]));
  const rowOfNumber = new Map<number, number>();
  const pending: { line: number; input: DishInput; kind: DishKind }[] = [];
  const planSheet = (dishSheet: Sheet, kind: DishKind) => {
    const h = findHead(dishSheet, DISH_COLUMNS);
    if (!h) errors.push({ sheet: dishSheet.name, line: 1, message: "Keine Kopfzeile mit „Name“ gefunden." });
    else {
      const drink = kind === "drink";
      const own = current.dishes.filter((d) => dishKind(d) === kind);
      const byName = new Map<string, Dish>();
      for (const d of [...own].sort((a, b) => a.number - b.number)) if (!byName.has(fold(d.name))) byName.set(fold(d.name), d);
      const has = (key: string) => h.columns.has(key);
      const trCols = translationColumns(dishSheet.rows[h.at] ?? []);
      const rowOfDish = new Map<string, number>();
      const list = drink ? drinkCategories : categories;
      dishSheet.rows.slice(h.at + 1).forEach((row, i) => {
        const line = h.at + 2 + i;
        if (isEmpty(row)) return;
        read[drink ? "drinks" : "dishes"]++;
        const at = (key: string) => row[h.columns.get(key) ?? -1];
        const issue = (message: string) => errors.push({ sheet: dishSheet.name, line, message });
        const rawNumber = cellText(at("number"));
        let number: number | null = null;
        if (rawNumber !== "") {
          const n = Number(rawNumber.replace(",", "."));
          if (!Number.isInteger(n) || n < 1 || n > MAX_NUMBER) return issue(`Nr. „${rawNumber}“: eine ganze Zahl von 1 bis ${MAX_NUMBER}`);
          number = n;
        }
        const name = cellText(at("name"));
        if (name === "") return issue("Der Name fehlt.");
        const price = has("price") ? parsePrice(at("price")) : { value: undefined };
        if ("error" in price) return issue(price.error);
        const parsed = parseCodes(at("allergens"), codes);
        if (parsed.unknown.length) return issue(`Unbekanntes Kürzel ${parsed.unknown.map((c) => `„${c}“`).join(", ")}. Bitte im Blatt „Allergene“ ergänzen.`);
        const variants = has("variants") ? parseVariants(cellText(at("variants")), codes, drink ? LIMITS.drinkVariants : LIMITS.variants) : null;
        if (variants && "error" in variants) return issue(`Weitere Preise: ${variants.error}`);
        const archived = has("archived") ? yesNo(at("archived")) : undefined;
        if (archived === "error") return issue(`Archiviert „${cellText(at("archived"))}“: ja oder nein`);
        const category = has("category") ? resolveCategory(cellText(at("category")), list) : null;
        if (category && "error" in category) return issue(`${category.error}. Bitte im Blatt „Kategorien“ ergänzen${drink ? " (für Getränke)" : ""}.`);
        const existing = number !== null ? byNumber.get(number) : byName.get(fold(name));
        if (existing && dishKind(existing) !== kind) return issue(`Die Nr. ${number} hat schon ${isDrink(existing) ? "das Getränk" : "die Speise"} „${existing.name}“.`);
        const key = existing?.id ?? `new:${number ?? fold(name)}`;
        if (rowOfDish.has(key)) return issue(`${drink ? "Dieses Getränk" : "Diese Speise"} steht schon in Zeile ${rowOfDish.get(key)}.`);
        if (number !== null && rowOfNumber.has(number)) return issue(`Die Nr. ${number} steht schon in Zeile ${rowOfNumber.get(number)}.`);
        rowOfDish.set(key, line);
        if (number !== null) rowOfNumber.set(number, line);
        const input: DishInput = {
          ...(drink ? { kind: "drink" as const } : {}),
          number: number ?? existing?.number ?? null,
          name,
          description: has("description") ? cellText(at("description")) : (existing?.description ?? ""),
          category: category ? category.value : (existing?.category ?? ""),
          tags: has("tags") ? cleanTags(cellText(at("tags"))) : (existing?.tags ?? []),
          allergens: parsed.codes ?? existing?.allergens ?? null,
          price: price.value === undefined ? (existing?.price ?? null) : price.value,
          priceLabel: has("priceLabel") ? cellText(at("priceLabel")) : (existing?.priceLabel ?? ""),
          variants: variants ? variants.value : (existing?.variants ?? []),
          archived: archived ?? existing?.archived ?? false,
        };
        // Further languages (S36): only the columns there are change; an unchanged translation keeps what it was made from.
        // „durchgesehen“ in its „Stand“ (S61) is the app's „Passt“: looked through, of the German as it is now.
        if (trCols.size) {
          const translations: TranslationsInput = { ...existing?.translations };
          for (const [lang, col] of trCols) {
            const before = existing?.translations?.[lang];
            const name = col.name !== undefined ? cellText(row[col.name]) : (before?.name ?? "");
            const description = col.description !== undefined ? cellText(row[col.description]) : (before?.description ?? "");
            const same = before && before.name === name && before.description === description;
            const seen = col.state !== undefined && seenIn(row[col.state]);
            translations[lang] = same ? (seen ? { name, description, by: before.by === "ai" ? "checked" : before.by } : before) : { name, description, by: "hand" };
          }
          input.translations = translations;
        }
        const clean = cleanDish(input, codes, list);
        if ("error" in clean) return issue(clean.error);
        if (existing) {
          const changes = describeChanges(existing, { ...clean.value, number: existing.number });
          if (changes.length === 0) unchanged++;
          else changed.push({ line, id: existing.id, number: existing.number, name: clean.value.name, changes, input: { ...clean.value, number: existing.number } });
        } else pending.push({ line, input: clean.value, kind });
      });
    }
  };
  if (dishSheet) planSheet(dishSheet, "food");
  if (drinkSheet) planSheet(drinkSheet, "drink");
  // New dishes and drinks without a number get the next free ones of their kind, after every number in use or in the sheets.
  const taken = new Set([...current.dishes.map((d) => d.number), ...rowOfNumber.keys()]);
  const numbersOf = (kind: DishKind) => [...current.dishes.filter((d) => dishKind(d) === kind).map((d) => d.number), ...pending.filter((p) => p.kind === kind && p.input.number != null).map((p) => p.input.number!)];
  const next = { food: Math.max(0, ...numbersOf("food")) + 1, drink: Math.max((current.drinkStart ?? DRINK_START) - 1, ...numbersOf("drink")) + 1 };
  for (const p of pending) {
    let number = p.input.number ?? null;
    if (number === null) {
      while (taken.has(next[p.kind]) && next[p.kind] < MAX_NUMBER) next[p.kind]++;
      number = next[p.kind]++;
      taken.add(number);
    }
    addedDishes.push({ line: p.line, input: { ...p.input, number } });
  }

  const texts = planTexts(textSheet, current.texts ?? [], errors);
  const phrases = planPhrases(words, current.phrases ?? {}, errors);
  return {
    categories: { added: addedCategories, result: categories },
    drinkCategories: { added: addedDrinkCategories, result: drinkCategories },
    allergens: { added, changed: changedAllergens, result },
    dishes: { added: addedDishes, changed, unchanged },
    texts: texts.plan,
    phrases: phrases.plan,
    errors,
    read: { dishes: read.dishes, drinks: read.drinks, allergens: readAllergens, categories: readCategories, texts: texts.read, phrases: phrases.read },
    empty: !dishSheet && !drinkSheet && !allergenSheet && !categorySheet && !textSheet && !words,
  };
}

const textLine = (s: string) => s.normalize("NFC").replace(/\s+/g, " ").trim();

/** The head of a sheet of texts: no column is a must, so the first of ten rows that names at least one. */
function textHead(sheet: Sheet): { at: number; columns: Map<string, number> } | null {
  for (let i = 0; i < Math.min(10, sheet.rows.length); i++) {
    const columns = columnsOf(sheet.rows[i] ?? [], TEXT_COLUMNS);
    if (columns && columns.size) return { at: i, columns };
  }
  return null;
}

/**
 * The sheet „Textbausteine“ (S14) against the texts of the catalogue: a row the same as a text is that text;
 * a heading that one text has, with other words, changes that text; any other row is new, at the
 * end. None go, none move (that is the app's).
 */
function planTexts(sheet: Sheet | undefined, current: readonly CardText[], errors: ImportIssue[]): { plan: ImportPlan["texts"]; read: number } {
  const result: (Omit<CardText, "id"> & { id?: string })[] = current.map((x) => ({ ...x }));
  const plan: ImportPlan["texts"] = { added: [], changed: [], unchanged: 0, result };
  if (!sheet) return { plan, read: 0 };
  const h = textHead(sheet);
  if (!h) {
    errors.push({ sheet: sheet.name, line: 1, message: "Keine Kopfzeile mit „Überschrift“ oder „Text“ gefunden." });
    return { plan, read: 0 };
  }
  const head = h;
  let read = 0;
  const seen = new Map<string, number>();
  sheet.rows.slice(head.at + 1).forEach((row, i) => {
    const line = head.at + 2 + i;
    if (isEmpty(row)) return;
    read++;
    const title = textLine(cellText(row[head.columns.get("title") ?? -1]));
    const body = textLine(cellText(row[head.columns.get("body") ?? -1]));
    const issue = (message: string) => errors.push({ sheet: sheet.name, line, message });
    if (!title && !body) return;
    if (title.length > TEXT_LIMITS.title) return issue(`Die Überschrift ist zu lang (höchstens ${TEXT_LIMITS.title} Zeichen)`);
    if (body.length > TEXT_LIMITS.body) return issue(`Der Text ist zu lang (höchstens ${TEXT_LIMITS.body} Zeichen)`);
    const key = `${title}\n${body}`;
    if (seen.has(key)) return issue(`Steht schon in Zeile ${seen.get(key)}`);
    seen.set(key, line);
    if (result.some((x) => x.title === title && x.body === body)) return void plan.unchanged++;
    const byTitle = title ? result.filter((x) => x.title === title) : [];
    if (byTitle.length === 1 && byTitle[0]!.id && !plan.changed.some((c) => c.id === byTitle[0]!.id)) {
      const known = byTitle[0]!;
      plan.changed.push({ line, id: known.id!, title, before: known.body, after: body });
      known.body = body;
      return;
    }
    if (result.length >= TEXT_LIMITS.texts) return issue(`Höchstens ${TEXT_LIMITS.texts} Textbausteine`);
    result.push({ title, body });
    plan.added.push({ line, title, body });
  });
  return { plan, read };
}

/** The language of a head in „Übersetzungen“: „Englisch (en)“, „Englisch“, „en“, „English“. */
function langOfHead(cell: Cell): Lang | null {
  const words = headKey(cell).split(" ");
  return LANGS.find((l) => words.includes(l)) ?? LANGS.find((l) => words.includes(fold(LANG_LABELS[l])) || words.includes(fold(LANG_NATIVE[l]))) ?? null;
}

/**
 * The sheet „Übersetzungen“ (S36) against the dictionary: per German text and language a filled cell
 * is the translation (new or changed), an empty one leaves it; the German text may be one the app does
 * not show (yet). Taking one away is the app's.
 */
function planPhrases(sheet: Sheet | undefined, current: Phrases, errors: ImportIssue[]): { plan: ImportPlan["phrases"]; read: number } {
  const plan: ImportPlan["phrases"] = { changed: [], unchanged: 0, checked: [] };
  if (!sheet) return { plan, read: 0 };
  const h = findHead(sheet, PHRASE_COLUMNS);
  if (!h) {
    errors.push({ sheet: sheet.name, line: 1, message: "Keine Kopfzeile mit „Deutsch“ gefunden." });
    return { plan, read: 0 };
  }
  const textAt = h.columns.get("text")!;
  // „Stand (en)“ (S61) beside its language, never taken for the language itself.
  const isState = (cell: Cell) => headKey(cell).split(" ")[0] === "stand";
  const heads = sheet.rows[h.at] ?? [];
  const states = new Map(heads.flatMap((cell, i) => (isState(cell) ? [[langOfHead(cell), i] as const] : [])).filter((x): x is [Lang, number] => x[0] !== null));
  const langs = heads.flatMap((cell, i) => {
    const lang = i === textAt || i === h.columns.get("group") || isState(cell) ? null : langOfHead(cell);
    return lang ? [{ lang, at: i, state: states.get(lang) }] : [];
  });
  if (!langs.length) errors.push({ sheet: sheet.name, line: h.at + 1, message: "Keine Spalte einer Sprache gefunden, etwa „Englisch (en)“." });
  let read = 0;
  const seen = new Map<string, number>();
  sheet.rows.slice(h.at + 1).forEach((row, i) => {
    const line = h.at + 2 + i;
    if (isEmpty(row)) return;
    read++;
    const text = phraseKey(cellText(row[textAt]));
    const issue = (message: string) => errors.push({ sheet: sheet.name, line, message });
    if (!text) return issue("Der deutsche Text fehlt");
    if (text.length > PHRASE_LIMITS.key) return issue("Der deutsche Text ist zu lang");
    if (seen.has(text)) return issue(`Steht schon in Zeile ${seen.get(text)}`);
    seen.set(text, line);
    for (const { lang, at, state } of langs) {
      const after = phraseKey(cellText(row[at]));
      if (!after) continue;
      if (after.length > PHRASE_LIMITS.text) {
        issue(`Die Übersetzung auf ${LANG_LABELS[lang]} ist zu lang (höchstens ${PHRASE_LIMITS.text} Zeichen)`);
        continue;
      }
      // The base vocabulary's as it was exported is no change: it stays the base, and a correction reaches it.
      const found = phraseOf(text, lang, current);
      const before = found?.text ?? "";
      if (before !== after) plan.changed.push({ line, text, lang, before, after });
      else if (found?.by === "ai" && state !== undefined && seenIn(row[state])) plan.checked.push({ line, text, lang });
      else plan.unchanged++;
    }
  });
  return { plan, read };
}

/** „Preis 5,20 → 5,50“, „Beschreibung“, „Allergene a → a, c“ … */
export function describeChanges(before: Dish, after: DishInput & { number: number }): string[] {
  const out: string[] = [];
  if (before.name !== after.name) out.push(`Name „${before.name}“ → „${after.name}“`);
  if (before.description !== (after.description ?? "")) out.push("Beschreibung");
  if (before.category !== (after.category ?? "")) out.push(`Kategorie ${before.category || "–"} → ${after.category || "–"}`);
  if (before.tags.join(", ") !== (after.tags ?? []).join(", ")) out.push(`Tags ${before.tags.join(", ") || "–"} → ${(after.tags ?? []).join(", ") || "–"}`);
  if (codesText(before.allergens) !== codesText(after.allergens ?? null)) out.push(`Allergene ${codesText(before.allergens) || "–"} → ${codesText(after.allergens ?? null) || "–"}`);
  if (before.price !== (after.price ?? null)) out.push(`Preis ${plainPrice(before.price) || "–"} → ${plainPrice(after.price ?? null) || "–"}`);
  if (before.priceLabel !== (after.priceLabel ?? "")) out.push(`Preis-Bezeichnung ${before.priceLabel || "–"} → ${after.priceLabel || "–"}`);
  const variants = (v: DishInput["variants"]) => variantsText((v ?? []).map((x) => ({ label: x.label, price: x.price ?? null, allergens: x.allergens ?? [] })));
  if (variants(before.variants) !== variants(after.variants)) out.push(`Weitere Preise ${variants(before.variants) || "–"} → ${variants(after.variants) || "–"}`);
  if (before.archived !== (after.archived ?? false)) out.push(after.archived ? "archiviert" : "wieder zur Auswahl");
  if (after.translations !== undefined)
    for (const l of LANGS) {
      const a = before.translations?.[l];
      const b = after.translations?.[l];
      if ((a?.name ?? "") !== (b?.name ?? "") || (a?.description ?? "") !== (b?.description ?? "")) out.push(LANG_LABELS[l]);
      // Looked through in the workbook (S61): by the model now checked, or out of date now current.
      else if (a && b && (a.by !== b.by || (b.source !== undefined && b.source !== a.source))) out.push(`${LANG_LABELS[l]} durchgesehen`);
    }
  return out;
}

/** „3 neu, 2 geändert, 45 gleich, 1 Fehler“ */
export function planSummary(plan: ImportPlan): string {
  const food = plan.dishes.added.filter((a) => a.input.kind !== "drink").length;
  const drinks = plan.dishes.added.length - food;
  const categories = plan.categories.added.length + plan.drinkCategories.added.length;
  const parts = [
    food ? `${food} ${food === 1 ? "Speise" : "Speisen"} neu` : "",
    drinks ? `${drinks} ${drinks === 1 ? "Getränk" : "Getränke"} neu` : "",
    plan.dishes.changed.length ? `${plan.dishes.changed.length} geändert` : "",
    plan.dishes.unchanged ? `${plan.dishes.unchanged} gleich` : "",
    categories ? `${categories} ${categories === 1 ? "Kategorie" : "Kategorien"} neu` : "",
    plan.allergens.added.length ? `${plan.allergens.added.length} Kürzel neu` : "",
    plan.allergens.changed.length ? `${plan.allergens.changed.length} Kürzel geändert` : "",
    plan.texts.added.length ? `${plan.texts.added.length} ${plan.texts.added.length === 1 ? "Textbaustein" : "Textbausteine"} neu` : "",
    plan.texts.changed.length ? `${plan.texts.changed.length} ${plan.texts.changed.length === 1 ? "Textbaustein" : "Textbausteine"} geändert` : "",
    plan.phrases.changed.length ? `${plan.phrases.changed.length} ${plan.phrases.changed.length === 1 ? "Übersetzung" : "Übersetzungen"} neu oder geändert` : "",
    plan.phrases.checked.length ? `${plan.phrases.checked.length} ${plan.phrases.checked.length === 1 ? "Übersetzung" : "Übersetzungen"} durchgesehen` : "",
    plan.errors.length ? `${plan.errors.length} Fehler` : "",
  ].filter(Boolean);
  return parts.join(", ") || "Nichts zu übernehmen";
}

/** Whether a plan changes anything. */
export const planChanges = (plan: ImportPlan) =>
  plan.dishes.added.length + plan.dishes.changed.length + plan.categories.added.length + plan.drinkCategories.added.length + plan.allergens.added.length + plan.allergens.changed.length + plan.texts.added.length + plan.texts.changed.length + plan.phrases.changed.length + plan.phrases.checked.length > 0;
